import React from 'react';
import { dataroomUrl } from '@dataroom/url';
import config from '@dataroom/config';
import stylesForSet from '../../Set/set.module.scss';
import Actions from './Actions/Actions';
import styles from '../datarooms.module.scss';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import { useDataroomAccessContext } from '../../../contexts/DataroomAccessContext';
import { IAllowedActions, IDataroomListItem } from '../../../domain/vo/DataroomListItem';
import IDataroomAction from './Actions/IDataroomAction';
import useUnfollowModal from '../Modals/UnfollowModal/useUnfollowModal';
import usePhysicalCopyModal from '@dataroom/physical-delivery/ui/PhysicalCopyModal/usePhysicalCopyModal';
import useMoveStageModal from '../Modals/MoveStageModal/useMoveStageModal';
import {
  documentationStateOptions,
} from '@dataroom/dataroom-form/ui/CreateDataroom/Forms/DataroomForm/DocumentationState/constants';
import dmPortalUrl from '@dm-portal/url';
import usersUrl from '@users/url';
import { ACTION_BUTTON_NAME } from './constants';
import { VariantType } from './Actions/ActionItem';
import IconDocument from '@dealroadshow/uikit-icon/IconDocument';
import IconSettings from '@dealroadshow/uikit-icon/IconSettings';
import IconPieChart from '@dealroadshow/uikit-icon/IconPieChart';
import IconDownload from '@dealroadshow/uikit-icon/IconDownload';
import IconUsers from '@dealroadshow/uikit-icon/IconUsers';
import IconFailed from '@dealroadshow/uikit-icon/IconFailed';

interface IProps {
  cellProps: {
    row: IDataroomListItem,
  },
  onSuccessUnfollowCallback?: () => void,
  isNext?: boolean,
  // @TODO: Remove after BE adds isRestrictedByCountry to allowedActions in dataroom.list endpoint
  isRestricted?: boolean,
}

function AllowedActions({
  cellProps,
  onSuccessUnfollowCallback,
  isNext,
  isRestricted = false,
}: IProps) {
  const { tenant } = useDataroomTenantContext();
  const { checkDataroomAccess } = useDataroomAccessContext();
  const {
    UnfollowModal,
    dataroomItem: dataroomUnfollowItem,
    showUnfollowModal,
    hideUnfollowModal,
  } = useUnfollowModal();

  const {
    Modal: PhysicalCopyModal,
    dataroomItem: dataroomPhysicalCopyItem,
    showPhysicalCopyModal,
    hidePhysicalCopyModal,
  } = usePhysicalCopyModal();

  const {
    MoveStageModal,
    dataroomItem: dataroomMoveStageItem,
    hideMoveStageModal,
    showMoveStageModal,
    isVisible,
  } = useMoveStageModal();

  const dataroom = cellProps.row;
  const { docState } = dataroom;
  const isFinal = documentationStateOptions.final.value === +docState;
  const {
    allowedActions: dataroomAllowedActions,
    isDeactivated,
  } = dataroom;
  const isSet = (
    tenant === config.investorset.code ||
    tenant === config.verisend.code ||
    tenant === config.researchroom.code
  );
  const isResearchRoom = tenant === config.researchroom.code;
  const tableStyles = isSet ? stylesForSet : styles;

  const getActionsConfig = (allowedActions: IAllowedActions): Array<IDataroomAction> => {
    const REQUEST_PHYSICAL_COPY = 'canRequestPhysicalCopy';
    const ANALYTICS = 'canViewAnalytics';
    const MANAGE_SETTINGS = 'canManageSettings';
    const MANAGE_USERS = 'canManageUsers';
    const BULK_DOWNLOAD = 'canBulkDownload';
    const UNFOLLOW = 'canUnfollow';
    const MOVE_STAGE = 'canMoveStage';

    const isDealVdr = tenant === config.dealvdr.code;
    const isResearchRoom = tenant === config.researchroom.code;
    const isVeriSend = tenant === config.verisend.code;
    const isPending = !!dataroom.accessRequest?.isPending;
    const isDenied = !!dataroom.accessRequest?.isDenied;
    const isLoansend = tenant === config.loansend.code;
    const {
      tenantSettingsPrefix,
      label,
      tenantUrlPrefix,
    } = config[tenant].options;

    const actionsMap = {
      [REQUEST_PHYSICAL_COPY]: (isAllowed: boolean) => {
        const isNotAvailable = isDenied || isPending || (isFinal && !isAllowed);
        let tooltip = isFinal ? 'Request a Physical Copy' : 'Physical Copies for Preliminary not available';
        if (isNotAvailable) tooltip = 'Physical Copies not available';

        if (isVeriSend) {
          return {
            disabled: !isFinal || isNotAvailable,
            icon: IconDocument,
            mobileTitle: 'Request a Physical Copy',
            tooltipContent: tooltip,
            onClick: () => showPhysicalCopyModal(dataroom),
            order: 0,
            dataTest: 'requestPhysicalCopyAction',
          };
        }
        return false;
      },
      [ANALYTICS]: (isAllowed: boolean) => {
        if (isDealVdr || isLoansend || (isSet && isAllowed)) {
          const path = `/${ tenant }/${ tenantUrlPrefix }/${ dataroom.id }/analytics`;

          return {
            disabled: !isAllowed,
            icon: IconPieChart,
            mobileTitle: 'Analytics',
            tooltipContent: isAllowed ? 'View Analytics' : 'Analytics not available',
            route: (isAllowed && !isNext) ? path : '',
            path: (isAllowed && isNext) ? dmPortalUrl.getUrl(path) : '',
            order: 1,
            dataTest: 'viewAnalyticsAction',
          };
        }
        return false;
      },
      [MANAGE_SETTINGS]: (isAllowed: boolean) => {
        if (isAllowed || (!isAllowed && !isSet) || (!isAllowed && isSet && isDeactivated)) {
          return {
            disabled: !isAllowed,
            icon: IconSettings,
            mobileTitle: 'Settings',
            tooltipContent: isAllowed ? 'Manage Settings' : 'Manage Settings not available',
            path: isAllowed ? usersUrl.getSessionPropagateUrl(dataroomUrl(tenant)
              .getUrl(`/${ tenantUrlPrefix }/${ dataroom.name }/settings/${ tenantSettingsPrefix }`)) : '',
            order: 2,
            dataTest: 'manageSettingsAction',
          };
        }
        return false;
      },
      [MANAGE_USERS]: (isAllowed: boolean) => {
        if (isAllowed || (!isAllowed && !isSet) || (!isAllowed && isSet && isDeactivated)) {
          const usersPermissionsUrl = usersUrl.getSessionPropagateUrl(
            dataroomUrl(tenant)
              .getUrl(`/${ tenantUrlPrefix }/${ dataroom.name }/settings/users-permissions`),
          );

          const handleManageUsersClick = async (e?: React.MouseEvent) => {
            e!.preventDefault();

            if (!isAllowed) return;

            if (e?.metaKey || e?.ctrlKey) {
              window.open(usersPermissionsUrl, '_blank');
              return;
            }

            if (await checkDataroomAccess(dataroom.id)) {
              window.location.href = usersPermissionsUrl;
            }
          };

          return {
            disabled: !isAllowed,
            icon: IconUsers,
            mobileTitle: 'Users',
            tooltipContent: isAllowed ? 'Manage Users' : 'Manage Users not available',
            onClick: handleManageUsersClick,
            path: isAllowed ? usersPermissionsUrl : '',
            order: 3,
            dataTest: 'manageUsersAction',
          };
        }
        return false;
      },
      [BULK_DOWNLOAD]: (isAllowed: boolean) => {
        const tooltipContentMapping: Partial<Record<string, { tooltipContent: string }>> = {
          [config.researchroom.code]: {
            tooltipContent: isAllowed ? 'Download' : 'Download not allowed',
          },
        };

        const handleBulkDownloadClick = async () => {
          if (!isAllowed) return;

          const downloadUrl = usersUrl.getSessionPropagateUrl(
            dataroomUrl(tenant)
              .getUrl(`/${ tenantUrlPrefix }/${ dataroom.name }/download`),
          );

          if (await checkDataroomAccess(dataroom.id)) {
            window.location.href = downloadUrl;
          }
        };

        return {
          disabled: !isAllowed,
          icon: IconDownload,
          mobileTitle: 'Bulk Download',
          tooltipContent: tooltipContentMapping[tenant]?.tooltipContent || (isAllowed ? `Bulk Download ${ label }` : 'Bulk Download Disabled'),
          onClick: handleBulkDownloadClick,
          path: '',
          order: 4,
          dataTest: 'bulkDownloadAction',
        };
      },
      [UNFOLLOW]: (isAllowed: boolean) => {
        const shouldRender = isAllowed || (!isAllowed && isPending);
        if (!shouldRender) return false;
        if (isResearchRoom) {
          return {
            disabled: !isAllowed,
            icon: IconFailed,
            mobileTitle: 'Unfollow',
            tooltipContent: isAllowed ? `Unfollow ${ label }` : `Unfollow ${ label } not available`,
            onClick: () => (isAllowed ? showUnfollowModal(dataroom) : false),
            order: 5,
            dataTest: 'unfollowAction',
          };
        }
        return {
          disabled: !isAllowed,
          icon: IconFailed,
          mobileTitle: 'Unfollow',
          tooltipContent: isAllowed ? `Unfollow ${ label }` : `Unfollow ${ label } not available`,
          onClick: () => (isAllowed ? showUnfollowModal(dataroom) : false),
          order: 5,
          dataTest: 'unfollowAction',
        };
      },
      [MOVE_STAGE]: (isAllowed: boolean) => {
        const buttonState = dataroom?.stage?.next;
        if (!isResearchRoom || !buttonState || !isAllowed) return false;

        const canMoveNext =
          Array.isArray(dataroom?.stage?.steps)
          || Object.values(dataroom?.stage?.steps)
            .every(Boolean);
        return {
          disabled: !isAllowed || !canMoveNext,
          mobileTitle: 'Go to next stage',
          variant: VariantType.Button,
          buttonTitle: (ACTION_BUTTON_NAME as Record<string, string>)[buttonState],
          onClick: () => (isAllowed ? showMoveStageModal(dataroom) : false),
          order: 6,
          dataTest: 'moveStageButton',
          disabledButtonTooltip: 'All steps must be completed to go Live.',
        };
      },
    };

    // @TODO: Remove after BE adds isRestrictedByCountry to allowedActions in dataroom.list endpoint
    return (Object.keys(actionsMap) as Array<keyof typeof actionsMap>)
      .reduce((actionsAcc: (IDataroomAction & { order: number })[], key) => {
        const actionConfig = actionsMap[key] && actionsMap[key](allowedActions[key]!);
        if (actionConfig) {
          if (isRestricted && key !== UNFOLLOW) {
            actionsAcc.push({
              ...actionConfig,
              disabled: true,
              path: '',
              route: '',
              onClick: undefined,
            });
          } else {
            actionsAcc.push(actionConfig);
          }
        }
        return actionsAcc.sort((a, b) => a.order - b.order);
      }, []);
  };

  return (
    <>
      <Actions
        cellProps={ cellProps }
        actionsConfig={ getActionsConfig(dataroomAllowedActions) }
        mobileClassName={ tableStyles.mobileDataroomActions }
        desktopClassName={ tableStyles.dataroomActions }
        twoFactorSpanClassname={ tableStyles.spanLink }
      />
      <UnfollowModal
        dataroom={ dataroomUnfollowItem! }
        onClose={ hideUnfollowModal }
        onSuccessCallback={ onSuccessUnfollowCallback }
      />
      <PhysicalCopyModal
        dataroom={ dataroomPhysicalCopyItem! }
        onClose={ hidePhysicalCopyModal }
      />
      { (isResearchRoom && isVisible) && (
        <MoveStageModal
          dataroom={ dataroomMoveStageItem! }
          onClose={ hideMoveStageModal }
        />
      ) }
    </>
  );
}

export default AllowedActions;
